/**
 * Transmission de la selection entre l'accueil et la fiche produit.
 *
 * La fiche produit reste une landing page autonome : elle recoit du trafic
 * direct (Shopping, Meta, SEO) et ne depend d'aucun etat construit ailleurs.
 * C'est Shopify qui pre-selectionne la variante passee en `?variant=`, comme
 * pour n'importe quel lien de catalogue. Ce composant ne fait que produire ce
 * lien, et le relire au retour.
 *
 * Trois responsabilites, toutes sur la section `featured-product` (accueil et
 * page collection) — la fiche produit, elle, a deja `data-update-url="true"` et
 * Dawn y ecrit l'URL tout seul :
 *
 *   1. reconstruire le lien « details » a chaque changement de selection ;
 *   2. `history.replaceState` avec le meme `?variant=`, pour que la selection
 *      survive au retour arriere et que l'URL soit partageable ;
 *   3. au chargement, relire `?variant=` — ou la variante par defaut reglee
 *      dans l'editeur — et amener le configurateur dessus.
 *
 * La selection passe par les radios du configurateur, jamais par une logique de
 * variante dupliquee : on coche ce qu'il faut puis on emet UN seul `change`.
 * VariantSelects lit alors l'etat complet des options et product-info.js ne
 * fait qu'une requete, meme quand deux options changent a la fois.
 */
class GoVariantUrl extends HTMLElement {
  connectedCallback() {
    this.link = this.querySelector('[data-details-link]');

    try {
      this.variants = JSON.parse(this.querySelector('[data-variant-map]')?.textContent || '{}');
    } catch {
      this.variants = {};
    }

    // Le lien part avec la variante rendue par le serveur : juste, tant qu'une
    // autre n'est pas demandee dans l'URL. Dans ce cas on ne touche a rien
    // avant la restauration, sinon on ecraserait le `?variant=` recu.
    if (!this.wanted) this.sync();

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
        if (data?.sectionId && data.sectionId !== this.dataset.section) return;
        this.sync();
      });
    }

    // Filet : `variantChange` n'arrive pas si `dispatchProductSelectEvent()`
    // leve avant le `publish` de Dawn (voir la note plus bas). La colonne
    // d'information, elle, est reellement reecrite a chaque changement, et
    // `sync` est deja garde contre les appels sans effet.
    const info = document.getElementById(`ProductInfo-${this.dataset.section}`);
    if (info && typeof MutationObserver !== 'undefined') {
      this.observer = new MutationObserver(() => this.sync());
      this.observer.observe(info, { childList: true, subtree: true });
    }

    // `product-info` doit avoir fini son `connectedCallback` avant qu'on coche
    // quoi que ce soit : c'est la qu'il s'abonne au bus. Emis plus tot, le
    // `change` ne serait ecoute par personne et la variante demandee dans l'URL
    // serait cochee sans que le prix ni la galerie suivent.
    // Le dispatch attend le chargement complet. `VariantSelects` appelle
    // `dispatchProductSelectEvent()` sur chaque `change`, qui touche a
    // `window.StandardEvents` — le module d'analytics de Shopify, charge en
    // parallele. Emis avant qu'il soit pret, l'evenement leve une exception
    // DANS le gestionnaire de Dawn, avant son `publish` : la valeur est cochee
    // mais ni le prix ni la galerie ne suivent. Constate a l'usage, sur un
    // rendu ou tout le reste etait pourtant en place.
    this.whenReady(() => {
      if (!this.restore()) this.sync();
    });
  }

  whenReady(callback) {
    if (document.readyState === 'complete') {
      callback();
      return;
    }
    window.addEventListener('load', callback, { once: true });
  }

  disconnectedCallback() {
    this.unsubscribe?.();
    this.observer?.disconnect();
  }

  get picker() {
    return document.querySelector(`variant-selects[data-section="${this.dataset.section}"]`);
  }

  /** L'identifiant de la variante en cours, tel que le serveur vient de le rendre. */
  get selectedId() {
    try {
      return String(JSON.parse(this.picker?.querySelector('[data-selected-variant]')?.textContent)?.id || '');
    } catch {
      return '';
    }
  }

  /* ------------------------------------------------------------ au retour */

  /** La variante demandee dans l'URL, ou celle reglee par defaut. */
  get wanted() {
    const asked = new URLSearchParams(window.location.search).get('variant') || this.dataset.defaultVariant;
    if (!asked || !this.variants[asked] || this.selectedId === String(asked)) return null;
    return asked;
  }

  /** @returns {Boolean} true si une selection a ete demandee au configurateur. */
  restore() {
    // Le reglage ne sert que d'entree par defaut : une variante explicitement
    // demandee dans l'URL passe toujours devant.
    const wanted = this.wanted;
    if (!wanted) return false;

    return this.select(this.variants[wanted]);
  }

  /**
   * Coche les valeurs d'une variante puis emet un seul `change`.
   *
   * Un `click()` par valeur declencherait une requete par option, dont une vers
   * une combinaison intermediaire qui n'est pas celle demandee.
   */
  select(values) {
    const picker = this.picker;
    if (!picker) return false;

    const inputs = Array.from(picker.querySelectorAll('input[type="radio"]'));
    let last = null;

    values.forEach((value) => {
      const input = inputs.find((candidate) => candidate.value === value);
      if (!input || input.checked) return;

      input.checked = true;
      last = input;

      // Une valeur cochee dans un groupe de famille masque serait invisible :
      // on ouvre la famille avant de rendre la main.
      const steps = input.closest('go-config-steps');
      const group = input.closest('.go-group[data-family]');
      if (group?.dataset.family && typeof steps?.apply === 'function') steps.apply(group.dataset.family);
    });

    if (!last) return false;

    last.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  }

  /* --------------------------------------------------------------- au fil */

  sync() {
    const id = this.selectedId;
    if (!id) return;

    /*
     * ⚠ Garde obligatoire depuis que `sync` est aussi declenche par un
     * MutationObserver pose sur la colonne d'information.
     *
     * Ce composant VIT dans cette colonne, et `textContent = ...` remplace les
     * noeuds enfants — donc une mutation, meme quand la valeur est identique.
     * Sans cette sortie, chaque passage rearme l'observateur : observateur →
     * ecriture → observateur, et le thread principal gele. Constate au rendu,
     * page entierement figee. `go-sticky-atc` porte deja la meme note pour la
     * meme raison.
     */
    if (id === this.syncedId) return;
    this.syncedId = id;

    if (this.link && this.dataset.productUrl) {
      this.link.href = `${this.dataset.productUrl}?variant=${id}`;
    }


    // `replaceState` et non `pushState` : chaque coloris essaye n'a pas a
    // remplir l'historique, mais le dernier doit survivre au retour arriere.
    const url = new URL(window.location.href);
    if (url.searchParams.get('variant') === id) return;
    url.searchParams.set('variant', id);
    window.history.replaceState({}, '', url);
  }
}

// Meme garde que les autres composants GO! : `featured-product` est une section
// repetable, et deux balises <script src> identiques s'executent deux fois.
if (!customElements.get('go-variant-url')) {
  customElements.define('go-variant-url', GoVariantUrl);
}
